import { useCallback } from 'react';

import { InquisitiveActivitiesContentType } from 'global/graphql/scalars.type';
import { Box } from 'approot/shared/design-system/box/box.style';
import { NegativeButton } from 'approot/shared/styles/buttons/negative-button';
import { SettingSmallText } from '../settings.style';
import useGetLazyActivitiesChanges from './use-get-lazy-activities-changes';
import { isResponseWithAnswersUpdated } from './logic/is-response-with-answers-updated';

type Props = {
  contentId: number;
  contentType: InquisitiveActivitiesContentType;
  disabled: boolean;
  openReviewChangesModal: () => void;
  openPublishMajorUpdateModal: () => void;
  setIsCheckingForChanges: (state: boolean) => void;
};

const SettingsPublishMajor = ({
  contentId,
  contentType,
  disabled,
  openReviewChangesModal,
  openPublishMajorUpdateModal,
  setIsCheckingForChanges,
}: Props) => {
  const { loading, getActivitiesChanges } = useGetLazyActivitiesChanges({
    onError: () => {
      setIsCheckingForChanges(false);
    },
  });

  const onUpdateSelected = useCallback(async () => {
    setIsCheckingForChanges(true);

    const { data } = await getActivitiesChanges({
      fetchPolicy: 'cache-and-network',
      variables: {
        input: {
          contentId,
          contentType,
        },
      },
    });
    if (data?.getActivitiesChanges && data.getActivitiesChanges.length > 0) {
      if (isResponseWithAnswersUpdated(data.getActivitiesChanges)) {
        openReviewChangesModal();
      } else {
        openPublishMajorUpdateModal();
      }
    } else {
      openPublishMajorUpdateModal();
    }
  }, [
    contentId,
    contentType,
    getActivitiesChanges,
    setIsCheckingForChanges,
    openPublishMajorUpdateModal,
    openReviewChangesModal,
  ]);

  return (
    <>
      <Box $marginBottom={8}>
        <NegativeButton
          type="button"
          variant="outlined"
          loading={loading}
          onClick={onUpdateSelected}
          disabled={loading || disabled}
          data-selector="btn-publish-major-update"
        >
          Publish Major Update
        </NegativeButton>
      </Box>
      <SettingSmallText>
        For any updates that may impact how students engage with the lesson. Use
        this for new components, images, or changes that could affect student
        responses.
      </SettingSmallText>
    </>
  );
};

export default SettingsPublishMajor;
